/**
 * Small UI kit shared by the Settings and Approvals surfaces. Token-themed
 * (semantic `bg/surface/border/muted/primary/…` classes) so it is dark-mode
 * correct without per-utility `dark:` overrides, and responsive. The canonical
 * Modal now comes from the shared kit (`@/ui`); these locals only cover the
 * primitives the shared kit does not expose.
 */

import type { ReactNode, ButtonHTMLAttributes } from 'react';

import { useT } from '@/i18n';
import { Modal } from '@/ui';

export function Panel({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-token-lg border border-border bg-surface text-foreground shadow-elevation-1 ${className}`}
    >
      {children}
    </div>
  );
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50',
  secondary:
    'border border-border bg-surface text-foreground hover:bg-surface-2 disabled:opacity-50',
  ghost: 'text-muted hover:bg-surface-2 hover:text-foreground disabled:opacity-50',
  danger: 'bg-danger text-primary-foreground hover:bg-danger/90 disabled:opacity-50',
};

export function Button({
  variant = 'secondary',
  className = '',
  type = 'button',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      type={type === 'submit' ? 'submit' : 'button'}
      {...props}
      className={`inline-flex items-center justify-center gap-1.5 rounded-token px-3 py-1.5 text-sm font-medium shadow-elevation-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed ${BUTTON_VARIANTS[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Badge({
  children,
  className = '',
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
      title={title}
    >
      {children}
    </span>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted">{hint}</span>}
    </label>
  );
}

const CONTROL_CLASS =
  'w-full rounded-token border border-border bg-surface px-3 py-1.5 text-sm text-foreground placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60';

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${CONTROL_CLASS} ${props.className ?? ''}`} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${CONTROL_CLASS} font-mono ${props.className ?? ''}`} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${CONTROL_CLASS} ${props.className ?? ''}`} />;
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useT();
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
      {label ?? t('common.loading')}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
      <p className="text-sm font-medium text-foreground">{title}</p>
      {hint && <p className="mt-1 max-w-sm text-xs text-muted">{hint}</p>}
    </div>
  );
}

export function ErrorBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss?: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-token border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
      <span>{message}</span>
      {onDismiss && (
        <DismissButton onDismiss={onDismiss} />
      )}
    </div>
  );
}

/** Small "✕" dismiss control — split out so ErrorBanner can stay presentational
 *  while this piece pulls the localized "Fermer" label from the catalog. */
function DismissButton({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useT();
  return (
    <button
      type="button"
      onClick={onDismiss}
      className="shrink-0 text-danger/70 hover:text-danger"
      aria-label={t('common.close')}
    >
      ✕
    </button>
  );
}

export function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-token-lg border border-border bg-surface-2/50 px-3 py-2">
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-foreground">{value}</div>
    </div>
  );
}

/** Read-only badge shown to non-admins where writes are server-gated. */
export function ReadOnlyBadge() {
  const { t } = useT();
  return <Badge className="bg-surface-2 text-muted">{t('common.readOnly')}</Badge>;
}

/**
 * ConfirmDialog — a small themed confirmation built on the shared `@/ui` Modal,
 * for destructive or high-impact actions (replaces the native `window.confirm`
 * so the app stays on one dialog system). Actions live in the Modal footer; the
 * confirm button reflects a `busy` state and the dialog is guarded against
 * closing (Escape / backdrop / Annuler) while the action is in flight.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  busy = false,
  onConfirm,
  onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useT();
  // Labels default to the localized common vocabulary; callers can still
  // override with a specific verb (e.g. "Supprimer définitivement").
  const confirmText = confirmLabel ?? t('common.confirm');
  const cancelText = cancelLabel ?? t('common.cancel');
  const guardedClose = () => {
    if (!busy) onClose();
  };
  return (
    <Modal
      title={title}
      size="sm"
      onClose={guardedClose}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={guardedClose} disabled={busy}>
            {cancelText}
          </Button>
          <Button type="button" variant={tone} onClick={onConfirm} disabled={busy}>
            {busy ? t('common.processing') : confirmText}
          </Button>
        </>
      }
    >
      <div className="text-sm text-foreground">{message}</div>
    </Modal>
  );
}
